import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Autocomplete } from "@/components/ui/autocomplete";
import { useFocusCnaeSearch, type CnaeOption } from "@/hooks/useFocusCnaeSearch";

interface FocusCnaeAutocompleteProps {
  value?: string | null;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
}

export function FocusCnaeAutocomplete({
  value,
  onChange,
  placeholder,
  disabled,
}: FocusCnaeAutocompleteProps) {
  const { t } = useTranslation("erp");
  const [displayText, setDisplayText] = useState(value ?? "");
  const normalizedValue = value ?? "";

  const exactQuery = useFocusCnaeSearch({
    query: normalizedValue,
    enabled: normalizedValue.length >= 2,
  });

  const searchQuery = useFocusCnaeSearch({
    query: displayText,
    enabled: !disabled,
  });

  const options = useMemo(
    () =>
      (searchQuery.data?.options ?? []).map((o: CnaeOption) => ({
        value: o.value,
        label: o.label,
      })),
    [searchQuery.data],
  );

  useEffect(() => {
    if (!normalizedValue) {
      setDisplayText("");
      return;
    }
    const match = exactQuery.data?.options?.find((o) => o.value === normalizedValue);
    if (match) setDisplayText(match.label);
  }, [normalizedValue, exactQuery.data]);

  const isLoading = searchQuery.isFetching;

  return (
    <div className="relative">
      <Autocomplete
        options={options}
        value={displayText}
        selectedValue={normalizedValue}
        onChange={(text) => {
          setDisplayText(text);
          if (!text.trim()) onChange("");
        }}
        onSelect={(option) => {
          setDisplayText(option.label);
          onChange(option.value);
        }}
        placeholder={placeholder}
        disabled={disabled}
        disableLocalFilter
        emptyMessage={
          isLoading
            ? t("common.loading")
            : displayText.trim().length < 2
            ? t("itemForm.fiscal.nfse.cnae.typeToSearch")
            : t("itemForm.fiscal.nfse.cnae.noResults")
        }
      />
      {isLoading && (
        <Loader2 className="pointer-events-none absolute right-8 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}
